import { Button as HeadlessButton, type ButtonProps as HeadlessButtonProps } from '@headlessui/react';
import { clsx } from 'clsx';
import { type ComponentPropsWithoutRef, type ReactNode, type ForwardedRef } from 'react';
import { forwardRef } from 'react';
import TouchTarget from './TouchTarget';
import Link from '@/components/common/Link';

const base = [
    // Layout
    'relative isolate inline-flex items-center justify-center gap-x-2 font-semibold',
    // Focus
    'data-focus:outline data-focus:outline-2 data-focus:outline-offset-2 data-focus:outline-teal-500',
    // Disabled
    'data-disabled:opacity-50 data-disabled:cursor-not-allowed disabled:opacity-50 disabled:cursor-not-allowed',
    // Cursor
    'cursor-pointer',
    // Icon
    '*:data-[slot=icon]:shrink-0 *:data-[slot=icon]:self-center',
];

const variantStyles = {
    primary: {
        default:
            'bg-teal-600 text-white data-hover:bg-teal-700 data-active:bg-teal-700 hover:bg-teal-700 active:bg-teal-700 data-disabled:bg-neutral-400 data-disabled:text-neutral-800 disabled:bg-neutral-400 disabled:text-neutral-800',
        destructive:
            'bg-red-500 text-white data-hover:bg-red-600 data-active:bg-red-600 hover:bg-red-600 active:bg-red-600 data-disabled:bg-neutral-400 data-disabled:text-neutral-800 disabled:bg-neutral-400 disabled:text-neutral-800',
    },
    secondary: {
        default:
            'border border-neutral-300 bg-neutral-0 text-neutral-900 data-hover:bg-neutral-100 data-active:bg-neutral-100 hover:bg-neutral-100 active:bg-neutral-100',
        destructive:
            'border border-red-300 bg-neutral-0 text-red-600 data-hover:bg-red-50 data-active:bg-red-100 hover:bg-red-50 active:bg-red-100',
    },
    soft: {
        default:
            'border border-teal-200 bg-teal-50 text-teal-600 data-hover:bg-teal-100 data-active:bg-teal-100 hover:bg-teal-100 active:bg-teal-100',
        destructive:
            'border border-red-200 bg-red-50 text-red-600 data-hover:bg-red-100 data-active:bg-red-100 hover:bg-red-100 active:bg-red-100',
    },
    tertiary: {
        default:
            'bg-transparent text-blue-600 data-hover:text-blue-700 data-active:text-blue-700 hover:text-blue-700 active:text-blue-700',
        destructive:
            'bg-transparent text-red-600 data-hover:text-red-700 data-active:text-red-700 hover:text-red-700 active:text-red-700',
    },
    ghost: {
        default:
            'bg-transparent text-neutral-900 data-hover:bg-neutral-100 data-active:bg-neutral-100 hover:bg-neutral-100 active:bg-neutral-100',
        destructive:
            'bg-transparent text-red-600 data-hover:bg-red-50 data-active:bg-red-100 hover:bg-red-50 active:bg-red-100',
    },
    /**
     * Frosted overlay button — designed to sit on top of colored gradient cards.
     * Default: white text/border + bg-white/30; destructive: red-themed equivalent.
     */
    overlay: {
        default: 'bg-white/30 border border-white text-white data-hover:bg-white/40 hover:bg-white/40',
        destructive: 'bg-white/30 border border-red-300 text-red-100 data-hover:bg-white/40 hover:bg-white/40',
    },
};

const sizeStyles = {
    /** Inline text-link sizing — no padding, no fixed height. Pairs naturally with `variant="tertiary"`. */
    inline: 'h-auto p-0 gap-x-1 text-base *:data-[slot=icon]:size-4',
    sm: 'h-9 rounded-md px-2.5 py-1.5 text-base gap-x-1.5 *:data-[slot=icon]:size-4',
    md: 'h-[38px] gap-x-1.5 rounded-md px-2.5 py-1.5 text-base *:data-[slot=icon]:size-4',
    lg: 'h-11 rounded-lg px-4 py-2.5 text-base *:data-[slot=icon]:size-5',
};

export type ButtonVariant = keyof typeof variantStyles;
export type ButtonSize = keyof typeof sizeStyles;
export type ButtonIntent = 'default' | 'destructive';

export type ButtonProps = {
    variant?: ButtonVariant;
    size?: ButtonSize;
    intent?: ButtonIntent;
    /**
     * Escape hatch for bespoke-styled buttons (e.g. nav items, custom card actions).
     * Skips variant/size/intent styling so the caller controls appearance via className.
     */
    ignoreVariantStyle?: boolean;
    className?: string;
    children: ReactNode;
    alignStart?: boolean;
} & (Omit<HeadlessButtonProps, 'as' | 'className'> | Omit<ComponentPropsWithoutRef<typeof Link>, 'className'>);

const Button = forwardRef(function Button(
    {
        variant = 'primary',
        size = 'md',
        intent = 'default',
        ignoreVariantStyle,
        className,
        children,
        alignStart,
        ...props
    }: ButtonProps,
    ref: ForwardedRef<HTMLElement>
) {
    const classes = ignoreVariantStyle
        ? clsx(base, sizeStyles[size], alignStart && 'justify-start', className)
        : clsx(base, sizeStyles[size], variantStyles[variant][intent], alignStart && 'justify-start', className);

    return 'to' in props ? (
        <Link {...props} className={classes} ref={ref as ForwardedRef<HTMLAnchorElement>}>
            <TouchTarget>{children}</TouchTarget>
        </Link>
    ) : (
        <HeadlessButton type="button" {...props} className={clsx(classes, 'cursor-default')} ref={ref}>
            <TouchTarget>{children}</TouchTarget>
        </HeadlessButton>
    );
});

export default Button;
